"use client";

import React from "react";
import { Icon } from "@iconify/react";

/** Matches uploaded-image icon values (http(s) URLs, data: images, blob: URLs). */
const IMAGE_ICON_URL_RE = /^(https?:\/\/|data:image\/|blob:)/i;

/**
 * Optional colour suffix on a monochrome Iconify value: "tabler:home|red".
 *
 * The colour lives in the icon string because every surface (callout attrs,
 * page `value.icon`, workarea icons) stores a single string and renders through
 * this one function — so one parse point colours them all.
 *
 * Only matches an Iconify id, so an image URL (contains "/" and ".") is never
 * split. Values written before this have no suffix → `color: null` → unchanged.
 */
const ICONIFY_WITH_COLOR_RE = /^([a-z0-9-]+:[a-z0-9-]+)\|([a-z]+)$/i;

/**
 * Selectable colours, on the app's own text-colour tokens. Stores the ID
 * ("red"), not the CSS value, so icons follow the theme in light and dark.
 */
export const ICON_COLORS: { id: string; label: string; cssValue: string }[] = [
  { id: "gray", label: "Gray", cssValue: "var(--novel-text-gray)" },
  { id: "brown", label: "Brown", cssValue: "var(--novel-text-brown)" },
  { id: "orange", label: "Orange", cssValue: "var(--novel-text-orange)" },
  { id: "yellow", label: "Yellow", cssValue: "var(--novel-text-yellow)" },
  { id: "green", label: "Green", cssValue: "var(--novel-text-green)" },
  { id: "blue", label: "Blue", cssValue: "var(--novel-text-blue)" },
  { id: "purple", label: "Purple", cssValue: "var(--novel-text-purple)" },
  { id: "pink", label: "Pink", cssValue: "var(--novel-text-pink)" },
  { id: "red", label: "Red", cssValue: "var(--novel-text-red)" },
];

const ICON_COLOR_CSS = new Map(ICON_COLORS.map((c) => [c.id, c.cssValue]));

/** Splits "tabler:home|red" into its icon and colour. Colour is null if absent. */
export function parseIconValue(value: string): { icon: string; color: string | null } {
  const match = value.match(ICONIFY_WITH_COLOR_RE);
  if (!match?.[1] || !match[2]) return { icon: value, color: null };
  // Unknown colour id (renamed token) → inherit, rather than rendering the raw
  // suffix as part of the icon name.
  const color = ICON_COLOR_CSS.has(match[2]) ? match[2] : null;
  return { icon: match[1], color };
}

/** Joins an icon and colour back into a storable value. */
export function buildIconValue(icon: string, color: string | null): string {
  return color && ICON_COLOR_CSS.has(color) ? `${icon}|${color}` : icon;
}

/**
 * True only for a monochrome Iconify glyph. Emoji, uploaded images and the
 * flat-color-icons set carry baked-in colours, so `currentColor` does nothing.
 */
export function supportsIconColor(value: string): boolean {
  if (!value || IMAGE_ICON_URL_RE.test(value)) return false;
  const { icon } = parseIconValue(value);
  return icon.includes(":") && !icon.startsWith("flat-color-icons:");
}

/** Resolves an icon value's colour to a CSS value, or undefined to inherit. */
export function iconColorCss(color: string | null): string | undefined {
  return color ? ICON_COLOR_CSS.get(color) : undefined;
}

/**
 * Renders an icon value:
 * - An image URL (uploaded icon, e.g. a workspace icon) → <img>.
 * - A value containing ":" → an Iconify icon string (e.g. "tabler:home"),
 *   optionally colour-suffixed ("tabler:home|red").
 * - Anything else → plain text (emoji).
 */
export function renderIcon(
  value: string,
  options?: { className?: string; style?: React.CSSProperties }
): React.ReactNode {
  if (!value) return null;
  // Must run before the Iconify check below — image URLs also contain ":".
  if (IMAGE_ICON_URL_RE.test(value)) {
    return (
      <img
        src={value}
        alt=""
        className={options?.className}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "inherit",
          display: "block",
          ...options?.style,
        }}
      />
    );
  }
  if (value.includes(":")) {
    const { icon, color } = parseIconValue(value);
    return (
      <Icon
        icon={icon}
        className={options?.className}
        // `color` first so a caller style still wins. Undefined when no colour
        // is stored, leaving the glyph on currentColor exactly as before.
        style={{ color: iconColorCss(color), ...options?.style }}
      />
    );
  }
  return (
    <span
      style={{ lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", ...options?.style }}
      className={options?.className}
    >
      {value}
    </span>
  );
}
